import { useCallback } from 'react';
import { stringify } from 'query-string';
import { stringifyOptions } from '@atlassian/jira-servicedesk-reports-common/src/common/constants';
import type {
	DateQuery,
	TimescaleQueryParam,
} from '@atlassian/jira-servicedesk-reports-common/src/common/helpers/types';
import { isCustomTimeScale } from '@atlassian/jira-servicedesk-reports-common/src/common/summary/main';
import type { ReportAppProps } from '@atlassian/jira-servicedesk-reports-common/src/ui/date-filter/types.tsx';
import type { ProjectKey, BaseUrl } from '@atlassian/jira-shared-types/src/general.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import type { SeriesData } from '../services/summary/utils/types.tsx';
import { APP_DEFAULTS } from './constants';

export const useUrlActions = ({
	baseUrl,
	projectKey,
	routerProps: { reportId },
}: ReportAppProps) => {
	const [{ location: pathname }, { push }] = useRouter();
	const modifyPageUrl = useCallback(
		(query: DateQuery) => {
			const queryString = stringify(
				{
					startTime: query.startDate,
					endTime: query.endDate,
					timeBreakdown: query.timeBreakdown,
				},
				// @ts-expect-error - TS2559 - Type '{ readonly skipNull: true; readonly sort: false; }' has no properties in common with type 'StringifyOptions'.
				stringifyOptions,
			);

			const timescaleId = query.timescaleId !== undefined ? query.timescaleId.toString() : '';

			const targetUrl = `${baseUrl}/jira/servicedesk/projects/${projectKey}/reports/${reportId}/timescale/${timescaleId}${
				queryString ? `?${queryString}` : ''
			}`;

			// @ts-expect-error - TS2367 - This condition will always return 'true' since the types 'Location' and 'string' have no overlap.
			if (pathname !== targetUrl) {
				push(targetUrl);
			}
		},
		[baseUrl, pathname, projectKey, push, reportId],
	);
	return { modifyPageUrl };
};

const getDrillDrownCsvUrlForCustomTimeScale = (
	urlPrefix: string,
	{ reportId, timeBreakdown }: TimescaleQueryParam,
	{ id, seriesId }: SeriesData,
) =>
	`${urlPrefix}/series-date-range/${reportId}/${seriesId}/csv?${stringify(
		{
			date: id,
			timeBreakdown,
		},
		// @ts-expect-error - TS2559 - Type '{ readonly skipNull: true; readonly sort: false; }' has no properties in common with type 'StringifyOptions'.
		stringifyOptions,
	)}`;

const getCsvUrlForCustomTimeScale = (
	urlPrefix: string,
	{ reportId, startDate, endDate, timeBreakdown }: TimescaleQueryParam,
) =>
	`${urlPrefix}/report-date-range/${reportId}/csv?${stringify(
		{
			startDate,
			endDate,
			timeBreakdown,
		},
		// @ts-expect-error - TS2559 - Type '{ readonly skipNull: true; readonly sort: false; }' has no properties in common with type 'StringifyOptions'.
		stringifyOptions,
	)}`;

const getCsvUrlForTimescale = (
	urlPrefix: string,
	{ reportId }: TimescaleQueryParam,
	timescaleId: number,
) =>
	`${urlPrefix}/report/predefined/${reportId}/csv?${stringify(
		{
			timescaleId,
		},
		// @ts-expect-error - TS2559 - Type '{ readonly skipNull: true; readonly sort: false; }' has no properties in common with type 'StringifyOptions'.
		stringifyOptions,
	)}`;

const getDrillDownCsvUrlForTimescale = (
	urlPrefix: string,
	{ reportId }: TimescaleQueryParam,
	timescaleId: number,
	{ id, seriesId }: SeriesData,
) =>
	`${urlPrefix}/series/predefined/${reportId}/${seriesId}/csv?${stringify(
		{
			date: id,
			timescaleId,
		},
		// @ts-expect-error - TS2559 - Type '{ readonly skipNull: true; readonly sort: false; }' has no properties in common with type 'StringifyOptions'.
		stringifyOptions,
	)}`;

export const getCsvUrl = (
	baseUrl: BaseUrl,
	projectKey: ProjectKey,
	dateFilterQuery: TimescaleQueryParam,
	selectedDrillDownPoint?: SeriesData,
): string => {
	const timescaleId =
		dateFilterQuery.timescaleId === undefined
			? APP_DEFAULTS.searchByPastSevenDays
			: dateFilterQuery.timescaleId;

	const urlPrefix = `${baseUrl}/rest/servicedesk/reports/1/servicedesk/${projectKey}/export`;

	if (isCustomTimeScale(timescaleId)) {
		return selectedDrillDownPoint !== undefined
			? getDrillDrownCsvUrlForCustomTimeScale(urlPrefix, dateFilterQuery, selectedDrillDownPoint)
			: getCsvUrlForCustomTimeScale(urlPrefix, dateFilterQuery);
	}

	return selectedDrillDownPoint !== undefined
		? getDrillDownCsvUrlForTimescale(
				urlPrefix,
				dateFilterQuery,
				timescaleId,
				selectedDrillDownPoint,
			)
		: getCsvUrlForTimescale(urlPrefix, dateFilterQuery, timescaleId);
};

export const getCsvForGeneratedReport = (
	baseUrl: BaseUrl,
	projectKey: ProjectKey,
	dateFilterQuery: TimescaleQueryParam,
	selectedDrillDownPoint?: SeriesData,
	taskId?: string,
): string => {
	if (taskId !== undefined && selectedDrillDownPoint === undefined) {
		return `${baseUrl}/rest/servicedesk/reports/1/reports/async/${projectKey}/export/csv?&jobID=${taskId}`;
	}
	return getCsvUrl(baseUrl, projectKey, dateFilterQuery, selectedDrillDownPoint);
};
